import React, { useState, useMemo, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useApp, ROLE_LABELS } from '../../context/AppContext';
import { useVisibleNav } from './Sidebar';
import { Search, Star, History, X, LayoutGrid, Loader2, ArrowDown, Bell, Check, LogOut } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// section key → icon tint (bg + fg)
const GROUP_TINT = {
  common: 'bg-slate-100 text-slate-600',
  field: 'bg-blue-100 text-blue-700',
  fastag: 'bg-amber-100 text-amber-700',
  hitchzone: 'bg-cyan-100 text-cyan-700',
  prepaid_cards: 'bg-indigo-100 text-indigo-700',
  operations: 'bg-emerald-100 text-emerald-700',
  marketing: 'bg-rose-100 text-rose-700',
  automobile: 'bg-violet-100 text-violet-700',
  hr: 'bg-teal-100 text-teal-700',
  admin: 'bg-orange-100 text-orange-700',
};
const DEFAULT_TINT = 'bg-slate-100 text-slate-600';

const favKey = (uid) => `mobile_favs_${uid || 'anon'}`;
const recentKey = (uid) => `mobile_recent_${uid || 'anon'}`;
const readLS = (k) => { try { return JSON.parse(localStorage.getItem(k) || '[]'); } catch { return []; } };
const writeLS = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* noop */ } };

export const pushRecent = (uid, to) => {
  if (!to) return;
  const k = recentKey(uid);
  const list = readLS(k).filter((x) => x !== to);
  list.unshift(to);
  writeLS(k, list.slice(0, 8));
};

export default function MobileHome({ onProfile, onLogout }) {
  const navigate = useNavigate();
  const { currentUser, notifications, markNotifRead, markAllRead } = useApp();
  const groups = useVisibleNav();
  const uid = currentUser?.id;
  const [notifOpen, setNotifOpen] = useState(false);
  const unread = (notifications || []).filter((n) => !n.read).length;

  const [query, setQuery] = useState('');
  const [favs, setFavs] = useState(() => readLS(favKey(uid)));
  const [badges, setBadges] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [pullY, setPullY] = useState(0);
  const [nonce, setNonce] = useState(0);
  const pullStart = React.useRef(null);
  const recent = useMemo(() => readLS(recentKey(uid)), [uid, nonce]);

  const loadBadges = useCallback(async () => {
    if (!uid) return;
    try {
      const d = (await axios.get(`${API}/launcher/badges`, { params: { requester_id: uid } })).data;
      setBadges(d.badges || {});
    } catch (e) { /* noop */ }
  }, [uid]);

  useEffect(() => {
    if (!uid) return;
    loadBadges();
    const t = setInterval(loadBadges, 60000);
    return () => clearInterval(t);
  }, [uid, loadBadges]);

  const doRefresh = useCallback(async () => {
    setRefreshing(true);
    setNonce((n) => n + 1);        // re-read favorites + recent from storage
    setFavs(readLS(favKey(uid)));
    await loadBadges();
    setTimeout(() => { setRefreshing(false); setPullY(0); }, 350);
  }, [uid, loadBadges]);

  const PULL_THRESHOLD = 60;
  const onTouchStart = (e) => {
    pullStart.current = (window.scrollY || 0) <= 2 ? e.touches[0].clientY : null;
  };
  const onTouchMove = (e) => {
    if (pullStart.current == null || refreshing) return;
    const delta = e.touches[0].clientY - pullStart.current;
    if (delta > 0 && (window.scrollY || 0) <= 2) {
      setPullY(Math.min(delta * 0.5, 90));
    }
  };
  const onTouchEnd = () => {
    if (pullStart.current == null) return;
    pullStart.current = null;
    if (pullY >= PULL_THRESHOLD && !refreshing) doRefresh();
    else setPullY(0);
  };

  // flat lookup of every visible item by route (first occurrence wins so a
  // shared route like /revenue keeps its primary label, e.g. FASTag Onboarding)
  const itemByTo = useMemo(() => {
    const m = {};
    groups.forEach((g) => g.items.forEach((it) => { if (!m[it.to]) m[it.to] = { ...it, groupKey: g.key }; }));
    return m;
  }, [groups]);

  const open = useCallback((to) => { pushRecent(uid, to); navigate(to); }, [uid, navigate]);

  // Onboarding tiles (FASTag / Prepaid Card) share the /revenue route and are
  // distinguished only by `cat`. Set the active category before navigating so
  // the tile opens the RIGHT report (mirrors the desktop Sidebar behaviour).
  const openItem = useCallback((it) => {
    if (it && it.cat) {
      const base = it.to;
      const cur = localStorage.getItem('revenue_category') || 'fastag';
      localStorage.setItem('revenue_category', it.cat);
      pushRecent(uid, it.to);
      if (window.location.pathname.startsWith(base)) {
        if (cur !== it.cat) window.location.reload();
      } else {
        window.location.assign(base);
      }
      return;
    }
    open(it.to);
  }, [uid, open]);

  const toggleFav = useCallback((e, to) => {
    e.stopPropagation();
    setFavs((prev) => {
      const next = prev.includes(to) ? prev.filter((x) => x !== to) : [...prev, to];
      writeLS(favKey(uid), next);
      return next;
    });
  }, [uid]);

  const q = query.trim().toLowerCase();
  const filteredGroups = useMemo(() => {
    if (!q) return groups;
    return groups
      .map((g) => ({ ...g, items: g.items.filter((it) => (it.label || '').toLowerCase().includes(q)) }))
      .filter((g) => g.items.length > 0);
  }, [groups, q]);

  const favItems = favs.map((to) => itemByTo[to]).filter(Boolean);
  const recentItems = recent.map((to) => itemByTo[to]).filter(Boolean).slice(0, 6);
  const greeting = (() => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'; })();

  const Tile = ({ it, groupKey }) => {
    const Icon = it.icon;
    const tint = GROUP_TINT[groupKey] || DEFAULT_TINT;
    const pinned = favs.includes(it.to);
    const badge = badges[it.to] || 0;
    return (
      <button
        type="button"
        data-testid={`launcher-tile-${it.to.replace(/\//g, '')}${it.cat ? '-' + it.cat : ''}`}
        onClick={() => openItem(it)}
        className="relative bg-white border border-slate-100 rounded-2xl p-3 pt-3.5 flex flex-col items-center gap-2 shadow-[0_2px_8px_rgba(15,23,42,0.04)] active:scale-[0.96] transition-transform"
      >
        <span
          role="button"
          data-testid={`launcher-fav-${it.to.replace(/\//g, '')}`}
          onClick={(e) => toggleFav(e, it.to)}
          className="absolute top-1.5 right-1.5 p-1"
        >
          <Star className={`w-3.5 h-3.5 ${pinned ? 'text-amber-500 fill-amber-500' : 'text-slate-300'}`} />
        </span>
        <span className="relative">
          <span className={`w-12 h-12 rounded-[14px] flex items-center justify-center ${tint}`}>
            {Icon ? <Icon className="w-[22px] h-[22px]" strokeWidth={1.9} /> : <LayoutGrid className="w-[22px] h-[22px]" />}
          </span>
          {badge > 0 && (
            <span data-testid={`launcher-badge-${it.to.replace(/\//g, '')}`} className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-extrabold flex items-center justify-center border-2 border-white shadow">
              {badge > 99 ? '99+' : badge}
            </span>
          )}
        </span>
        <span className="text-[11.5px] font-semibold text-slate-700 text-center leading-tight line-clamp-2">{it.label}</span>
      </button>
    );
  };

  const SectionTitle = ({ icon: Ic, children }) => (
    <div className="flex items-center gap-2 px-4 pt-5 pb-2">
      {Ic && <Ic className="w-3.5 h-3.5 text-slate-400" />}
      <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-400">{children}</span>
      <span className="flex-1 h-px bg-slate-100 ml-1" />
    </div>
  );

  return (
    <div
      data-testid="mobile-home"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      className="-mx-4 -mt-3 min-h-screen bg-slate-50"
      style={{ transform: `translateY(${pullY}px)`, transition: pullStart.current != null ? 'none' : 'transform .25s ease' }}
    >
      {/* pull-to-refresh indicator */}
      {(pullY > 0 || refreshing) && (
        <div
          data-testid="launcher-pull-indicator"
          className="fixed left-1/2 -translate-x-1/2 top-2 z-30 w-9 h-9 rounded-full bg-white shadow-md border border-slate-200 flex items-center justify-center"
          style={{ opacity: refreshing ? 1 : Math.min(pullY / PULL_THRESHOLD, 1) }}
        >
          {refreshing
            ? <Loader2 className="w-4 h-4 text-amber-600 animate-spin" />
            : <ArrowDown className={`w-4 h-4 text-amber-600 transition-transform ${pullY >= PULL_THRESHOLD ? 'rotate-180' : ''}`} />}
        </div>
      )}
      {/* header */}
      <div className="px-4 pt-3 pb-1 bg-slate-50">
        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="launcher-profile-btn"
            onClick={() => onProfile && onProfile()}
            className="flex items-center gap-3 min-w-0 active:scale-95 transition-transform text-left"
          >
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-700 text-white flex items-center justify-center font-extrabold text-sm shadow-lg shadow-amber-600/30">
              {currentUser?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="min-w-0">
              <div className="text-[12px] text-slate-400 font-semibold leading-none">{greeting}</div>
              <div className="text-[16px] font-extrabold text-slate-900 leading-tight truncate">{currentUser?.name || 'User'}</div>
            </div>
          </button>
          <span className="ml-auto text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-1 rounded-full">
            {ROLE_LABELS[currentUser?.role] || currentUser?.role}
          </span>
          <button
            type="button"
            data-testid="launcher-bell-btn"
            onClick={() => setNotifOpen(true)}
            className="relative w-10 h-10 rounded-2xl bg-white border border-slate-200 flex items-center justify-center text-slate-600 active:scale-95 transition-transform shadow-sm"
          >
            <Bell className="w-5 h-5" strokeWidth={1.8} />
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-extrabold flex items-center justify-center border-2 border-white">
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </button>
        </div>
        {/* search */}
        <div className="mt-3 flex items-center gap-2 bg-white border border-slate-200 rounded-2xl px-3.5 py-2.5 shadow-sm">
          <Search className="w-4 h-4 text-slate-400" />
          <input
            data-testid="launcher-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tools…"
            className="flex-1 bg-transparent outline-none text-sm text-slate-700 placeholder:text-slate-400"
          />
          {query && <button onClick={() => setQuery('')} data-testid="launcher-search-clear"><X className="w-4 h-4 text-slate-400" /></button>}
        </div>
      </div>

      {/* favorites */}
      {!q && favItems.length > 0 && (
        <>
          <SectionTitle icon={Star}>Favorites</SectionTitle>
          <div className="flex gap-3 overflow-x-auto px-4 pb-1 no-scrollbar">
            {favItems.map((it) => {
              const Icon = it.icon; const tint = GROUP_TINT[it.groupKey] || DEFAULT_TINT;
              const badge = badges[it.to] || 0;
              return (
                <button
                  key={it.to}
                  data-testid={`launcher-favtile-${it.to.replace(/\//g, '')}`}
                  onClick={() => openItem(it)}
                  className="flex-shrink-0 w-[72px] flex flex-col items-center gap-1.5 active:scale-95 transition-transform"
                >
                  <span className="relative">
                    <span className={`w-14 h-14 rounded-[18px] flex items-center justify-center shadow-md ${tint}`}>
                      {Icon ? <Icon className="w-6 h-6" strokeWidth={1.9} /> : <LayoutGrid className="w-6 h-6" />}
                    </span>
                    {badge > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-extrabold flex items-center justify-center border-2 border-white shadow">
                        {badge > 99 ? '99+' : badge}
                      </span>
                    )}
                  </span>
                  <span className="text-[10px] font-bold text-slate-600 text-center leading-tight line-clamp-2">{it.label}</span>
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* recent */}
      {!q && recentItems.length > 0 && (
        <>
          <SectionTitle icon={History}>Recent</SectionTitle>
          <div className="flex gap-2 overflow-x-auto px-4 pb-1 no-scrollbar">
            {recentItems.map((it) => {
              const Icon = it.icon;
              return (
                <button
                  key={it.to}
                  data-testid={`launcher-recent-${it.to.replace(/\//g, '')}`}
                  onClick={() => openItem(it)}
                  className="flex-shrink-0 flex items-center gap-2 bg-white border border-slate-200 rounded-full pl-2.5 pr-3.5 py-2 active:scale-95 transition-transform"
                >
                  {Icon && <Icon className="w-4 h-4 text-slate-500" strokeWidth={1.9} />}
                  <span className="text-xs font-bold text-slate-600 whitespace-nowrap">{it.label}</span>
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* grouped grid */}
      {filteredGroups.length === 0 ? (
        <div data-testid="launcher-empty" className="text-center text-sm text-slate-400 py-16 px-6">
          No tools match “{query}”.
        </div>
      ) : (
        filteredGroups.map((g) => (
          <div key={g.key} data-testid={`launcher-section-${g.key}`}>
            <SectionTitle icon={g.icon}>{g.label}</SectionTitle>
            <div className="grid grid-cols-3 gap-2.5 px-4">
              {g.items.map((it) => <Tile key={it.to} it={it} groupKey={g.key} />)}
            </div>
          </div>
        ))
      )}
      <div className="h-6" />

      {/* Notifications */}
      <Sheet open={notifOpen} onOpenChange={setNotifOpen}>
        <SheetContent side="right" className="w-[88vw] sm:w-96 p-0 flex flex-col" data-testid="launcher-notif-sheet">
          <SheetHeader className="p-4 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-base flex items-center gap-2"><Bell className="w-4 h-4 text-amber-600" /> Notifications</SheetTitle>
              {unread > 0 && (
                <button data-testid="launcher-notif-mark-all" onClick={() => markAllRead && markAllRead()} className="text-xs font-semibold text-amber-600">Mark all read</button>
              )}
            </div>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto">
            {(notifications || []).length === 0 ? (
              <div className="text-center text-sm text-slate-400 py-16">You're all caught up 🎉</div>
            ) : (
              (notifications || []).map((n) => (
                <button
                  key={n.id}
                  data-testid={`launcher-notif-${n.id}`}
                  onClick={() => { if (!n.read && markNotifRead) markNotifRead(n.id); if (n.url) { setNotifOpen(false); navigate(n.url); } }}
                  className={`w-full text-left px-4 py-3 border-b border-slate-100 flex items-start gap-3 ${!n.read ? 'bg-amber-50/50' : ''}`}
                >
                  <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${!n.read ? 'bg-amber-500' : 'bg-slate-200'}`} />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-slate-700">{n.message}</span>
                    <span className="block text-[10px] text-slate-400 mt-0.5">{n.notif_type}</span>
                  </span>
                  {!n.read && <Check className="w-4 h-4 text-slate-300 mt-0.5" />}
                </button>
              ))
            )}
          </div>
          {onLogout && (
            <button data-testid="launcher-logout" onClick={() => { setNotifOpen(false); onLogout(); }} className="m-3 flex items-center justify-center gap-2 text-sm font-semibold text-rose-600 border border-rose-200 rounded-xl py-2.5">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
